import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSatellite,
  faCheck,
  faSnowflake,
  faWrench,
  faTimes,
  faClock,
  faGlobe,
  faChartBar,
  faBell,
  faExclamationTriangle,
  faRefresh,
  faChevronDown,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import LoadingScreen from './LoadingScreen';
import apiService from '../services/api';

const ExternalSources = () => {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [externalSources, setExternalSources] = useState([]);

  // Load data from API
  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await apiService.getExternalSourcesDetailed();
        setExternalSources(data);
        const loadingTime = 1000 + Math.random() * 1500;
        setTimeout(() => setLoading(false), loadingTime);
      } catch (error) {
        console.error('Failed to load external sources:', error);
        setError('Failed to load external data sources. Please try again.');
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Scroll to specific source based on URL query parameter
  useEffect(() => {
    if (!loading) {
      const params = new URLSearchParams(window.location.search);
      const sourceId = params.get('id');

      if (sourceId) {
        setTimeout(() => {
          const element = document.getElementById(`source-${sourceId}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Expand the row automatically
            setExpandedRows(new Set([parseInt(sourceId)]));
            // Add highlight effect
            element.style.boxShadow = '0 0 20px rgba(59, 130, 246, 0.5)';
            setTimeout(() => {
              element.style.boxShadow = '';
            }, 2000);
          }
        }, 300);
      }
    }
  }, [loading]);

  // Calculate metrics from loaded data
  const sourceMetrics = {
    totalSources: externalSources.length,
    operational: externalSources.filter(s => s.status === 'operational').length,
    degraded: externalSources.filter(s => s.status === 'degraded').length
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'operational': return faCheck;
      case 'slow': return faSnowflake;
      case 'maintenance': return faWrench;
      case 'error': return faTimes;
      default: return faClock;
    }
  };

  const toggleRow = (sourceId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(sourceId)) {
      newExpanded.delete(sourceId);
    } else {
      newExpanded.add(sourceId);
    }
    setExpandedRows(newExpanded);
  };

  const getUptimeClass = (uptime) => {
    const percentage = parseFloat(uptime);
    if (percentage > 98) return 'success';
    if (percentage > 95) return 'warning';
    return 'error';
  };


  // Loading state
  if (loading) {
    return (
      <div className="external-sources-container">
        <LoadingScreen message="Loading external data sources..." />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="external-sources-container">
        <div className="sources-header">
          <h2>
            <FontAwesomeIcon icon={faSatellite} style={{ marginRight: '0.5rem' }} />
            External Data Sources
          </h2>
        </div>
        <div className="error-container">
          <FontAwesomeIcon icon={faExclamationTriangle} className="error-icon" />
          <h3>Connection Error</h3>
          <p>{error}</p>
          <button className="retry-btn" onClick={() => window.location.reload()}>
            <FontAwesomeIcon icon={faRefresh} style={{ marginRight: '0.5rem' }} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="external-sources-container">
      <div className="sources-header">
        <h2>
          <FontAwesomeIcon icon={faSatellite} style={{ marginRight: '0.5rem' }} />
          External Data Sources
        </h2>
        <div className="sources-stats">
          <div className="stat-item">
            <span className="stat-value">{sourceMetrics.totalSources}</span>
            <span className="stat-label">Total Sources</span>
          </div>
          <div className="stat-item">
            <span className="stat-value" style={{ color: '#10b981' }}>{sourceMetrics.operational}</span>
            <span className="stat-label">Operational</span>
          </div>
          <div className="stat-item">
            <span className="stat-value" style={{ color: '#f59e0b' }}>{sourceMetrics.degraded}</span>
            <span className="stat-label">Degraded</span>
          </div>
        </div>
      </div>

      {/* Sources Table */}
      <div className="data-table" style={{ maxWidth: '100%', width: '100%' }}>
        <div className="table-header">
          <h3 className="table-title">EO Data Provider Status</h3>
          <span className="table-subtitle">Real-time monitoring of external Earth Observation data providers</span>
        </div>
        <div className="table-content" style={{ overflowX: 'auto' }}>
          <table className="data-table-element" style={{ width: '100%', minWidth: '1200px' }}>
            <thead>
              <tr>
                <th style={{ width: '24%', padding: '1rem', textAlign: 'left' }}>Source Name</th>
                <th style={{ width: '11%', padding: '1rem', textAlign: 'left' }}>Status</th>
                <th style={{ width: '11%', padding: '1rem', textAlign: 'left' }}>Uptime (30d)</th>
                <th style={{ width: '13%', padding: '1rem', textAlign: 'left' }}>Avg Response</th>
                <th style={{ width: '13%', padding: '1rem', textAlign: 'left' }}>API Calls (24h)</th>
                <th style={{ width: '13%', padding: '1rem', textAlign: 'left' }}>Data Transferred</th>
                <th style={{ width: '11%', padding: '1rem', textAlign: 'left' }}>Last Check</th>
                <th style={{ width: '4%', padding: '1rem', textAlign: 'center' }}></th>
              </tr>
            </thead>
            <tbody>
              {externalSources.map((source) => (
                <React.Fragment key={source.id}>
                  <tr
                    id={`source-${source.id}`}
                    className={`expandable ${expandedRows.has(source.id) ? 'expanded' : ''}`}
                    onClick={() => toggleRow(source.id)}
                    style={{ transition: 'all 0.2s ease', cursor: 'pointer' }}
                  >
                    <td style={{ padding: '1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <FontAwesomeIcon icon={faSatellite} style={{ color: '#8b5cf6', fontSize: '1.25rem' }} />
                        <div>
                          <div style={{ fontWeight: 600, marginBottom: '0.25rem', fontSize: '0.95rem' }}>{source.name}</div>
                          <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{source.type}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <span className={`status-badge ${source.status}`}>
                        <FontAwesomeIcon
                          icon={getStatusIcon(source.status)}
                          style={{ marginRight: '0.35rem' }}
                        />
                        {source.status.toUpperCase()}
                      </span>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <span className={`severity-badge ${getUptimeClass(source.uptime)}`}>
                        {source.uptime}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', fontFamily: 'monospace', color: '#e2e8f0', fontSize: '0.9rem' }}>
                      {source.avgResponseTime}ms
                    </td>
                    <td style={{ padding: '1rem', fontFamily: 'monospace', color: '#e2e8f0', fontSize: '0.9rem' }}>
                      {source.apiCalls24h.toLocaleString()}
                    </td>
                    <td style={{ padding: '1rem', fontFamily: 'monospace', color: '#e2e8f0', fontSize: '0.9rem' }}>
                      {source.dataTransferred}
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.85rem', color: '#94a3b8' }}>
                      {source.lastCheck}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      <FontAwesomeIcon
                        icon={expandedRows.has(source.id) ? faChevronDown : faChevronRight}
                        style={{ color: '#8b5cf6', fontSize: '0.9rem' }}
                      />
                    </td>
                  </tr>

                  {expandedRows.has(source.id) && (
                    <tr className="expandable-row">
                      <td colSpan="8">
                        <div className="expandable-content">
                          <div className="source-details-grid">
                            {/* Connection Details */}
                            <div className="detail-section">
                              <h4>
                                <FontAwesomeIcon icon={faGlobe} style={{ marginRight: '0.5rem' }} />
                                Connection Details
                              </h4>
                              <div className="properties-grid">
                                <div className="property-item">
                                  <span className="prop-label">URL:</span>
                                  <span className="prop-value" style={{ wordBreak: 'break-all' }}>
                                    {source.url}
                                  </span>
                                </div>
                                <div className="property-item">
                                  <span className="prop-label">Service Type:</span>
                                  <span className="prop-value">{source.type}</span>
                                </div>
                                <div className="property-item">
                                  <span className="prop-label">Uptime (30d):</span>
                                  <span className={`prop-value ${getUptimeClass(source.uptime)}`}>
                                    {source.uptime}
                                  </span>
                                </div>
                                <div className="property-item">
                                  <span className="prop-label">Last Check:</span>
                                  <span className="prop-value">{source.lastCheck}</span>
                                </div>
                              </div>
                            </div>

                            {/* Performance Metrics */}
                            <div className="detail-section">
                              <h4>
                                <FontAwesomeIcon icon={faChartBar} style={{ marginRight: '0.5rem' }} />
                                Performance Metrics
                              </h4>
                              <div className="properties-grid">
                                <div className="property-item">
                                  <span className="prop-label">Average Response:</span>
                                  <span className="prop-value">{source.avgResponseTime}ms</span>
                                </div>
                                <div className="property-item">
                                  <span className="prop-label">API Calls (24h):</span>
                                  <span className="prop-value">{source.apiCalls24h.toLocaleString()}</span>
                                </div>
                                <div className="property-item">
                                  <span className="prop-label">Data Transferred:</span>
                                  <span className="prop-value">{source.dataTransferred}</span>
                                </div>
                                <div className="property-item">
                                  <span className="prop-label">Status:</span>
                                  <span className={`prop-value ${source.status}`}>
                                    {source.status.charAt(0).toUpperCase() + source.status.slice(1)}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Maintenance/Degraded Notice */}
                            {source.announcement && (
                              <div className="detail-section" style={{ gridColumn: '1 / -1' }}>
                                <h4>
                                  <FontAwesomeIcon icon={faBell} style={{ marginRight: '0.5rem', color: '#f59e0b' }} />
                                  {source.announcement.title}
                                </h4>
                                <div className="ai-suggestion" style={{ margin: 0 }}>
                                  <div className="ai-suggestion-header">
                                    <FontAwesomeIcon
                                      icon={source.announcement.type === 'maintenance' ? faWrench : faExclamationTriangle}
                                    />
                                    <strong>{source.announcement.type.toUpperCase()}</strong>
                                    <span className="confidence-badge">{source.announcement.eta}</span>
                                  </div>
                                  <div className="ai-suggestion-content">
                                    <p>{source.announcement.message}</p>
                                    <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#94a3b8' }}>
                                      <strong>Timestamp:</strong> {source.announcement.timestamp}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
};

export default ExternalSources;